import queryString from "query-string";
import {
  Component,
  ComponentClass,
  FC,
  useLayoutEffect,
  useState,
} from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { loadSection } from "../actions/common/currentSection";
import { dispatchResetPageErrors } from "../actions/common/errors";
import {
  checkPrivileges,
  loadOperatorPrivileges,
} from "../actions/common/operatorPrivileges";
import { resetApp } from "../actions/common/resetApp";
import { loadStudent } from "../actions/common/student";
import {
  showErrorFlashMessage,
  showSuccessFlashMessage,
} from "../actions/flashMessage";
import { useStudentFilterContext } from "../components/features/NewStudentFilter/useStudentFilterContext";
import { StudentTagFilterInterface } from "../domains/StudentTag";
import FiltersHelper from "../helpers/FiltersHelper";
import { WithRouterProps, withRouter } from "../helpers/RouterHelper";
import { useInitializeStudentTags } from "../hooks/filters/useInitializeStudentTags";
import { useQueryError } from "../hooks/http/useQueryError";
import AppStateInterface from "../interfaces/AppStateInterface";
import DateRangeFilterInterface from "../interfaces/DateRangeFilterInterface";
import ErrorsStateInterface from "../interfaces/ErrorsStateInterface";
import {
  FiltersQueryInterface,
  OrderDirFilterType,
} from "../interfaces/FiltersInterface";
import OperatorInterface, {
  OperatorPrivilege,
} from "../interfaces/OperatorInterface";
import OperatorPrivilegesStateInterface from "../interfaces/OperatorPrivilegesStateInterface";
import { SectionStateInterface } from "../interfaces/SectionInterface";
import StudentInterface, {
  StudentStateInterface,
} from "../interfaces/StudentInterface";

export interface AuthenticatedPageCallbacks {
  onDateRangeFilterChange: (dateRangeFilter: DateRangeFilterInterface) => void;
  onDateFilterChange: (dateFilter: Date) => void;
  onOrderChange: (order: string, orderDir: OrderDirFilterType) => void;
  onStudentFilterChange: (tagFilter: StudentTagFilterInterface) => void;
  onSearchStudent: (studentNameFilters: {
    firstName: string;
    lastName: string;
  }) => void;
}

export interface DispatchProps {
  resetApp: HandleThunkActionCreator<typeof resetApp>;
  loadSection: HandleThunkActionCreator<typeof loadSection>;
  loadStudent: HandleThunkActionCreator<typeof loadStudent>;
  loadOperatorPrivileges: HandleThunkActionCreator<
    typeof loadOperatorPrivileges
  >;
  dispatchResetPageErrors: HandleThunkActionCreator<
    typeof dispatchResetPageErrors
  >;
  checkPrivileges: HandleThunkActionCreator<typeof checkPrivileges>;
  showErrorFlashMessage: HandleThunkActionCreator<typeof showErrorFlashMessage>;
  showSuccessFlashMessage: HandleThunkActionCreator<
    typeof showSuccessFlashMessage
  >;
}

export interface StateProps {
  operators: OperatorInterface[];
  currentOperator: OperatorInterface | null;
  currentSection: SectionStateInterface;
  student: StudentStateInterface;
  errors: ErrorsStateInterface;
  operatorPrivileges: OperatorPrivilegesStateInterface;
  isLoggedIn: boolean;
}

export type AuthenticateRouterProps = { studentId: string; sectionId: string };

export interface AuthenticatedPageProps
  extends DispatchProps,
    StateProps,
    WithRouterProps<AuthenticateRouterProps> {
  studentFilterContext: ReturnType<typeof useStudentFilterContext>;
}

const mapStateToProps = (state: AppStateInterface) => {
  return {
    operators: state.session.loggedInOperators,
    currentOperator: state.session.currentOperator,
    errors: state.errors,
    currentSection: state.currentSection,
    student: state.student,
    operatorPrivileges: state.operatorPrivileges,
    isLoggedIn: state.session.isLoggedIn,
  };
};

const actions = {
  resetApp,
  dispatchResetPageErrors,
  loadSection,
  loadStudent,
  loadOperatorPrivileges,
  checkPrivileges,
  showErrorFlashMessage,
  showSuccessFlashMessage,
};

const enhanceAuthenticatedPage = (
  WrappedComponent: ComponentClass<any, any> | FC<any>,
  requiredPrivileges?: OperatorPrivilege[],
) => {
  class AuthenticatedPage extends Component<AuthenticatedPageProps> {
    get sectionId(): string | null {
      if (this.props.currentSection.data) {
        return this.props.currentSection.data.id;
      } else if (this.props.student.data) {
        return this.props.student.data.section.id;
      } else {
        return null;
      }
    }

    get studentFilter(): StudentTagFilterInterface {
      return this.props.studentFilterContext.findTagFilter({
        sectionId: this.sectionId || "",
      });
    }

    constructor(
      props: AuthenticatedPageProps & WithRouterProps<AuthenticateRouterProps>,
    ) {
      super(props);

      if (this.hasError()) {
        return;
      }

      if (!this.props.isLoggedIn || this.props.currentOperator) {
        // **校舎一覧画面以外**ではこの関数が実行されたとき
        // - 校舎情報 currentSection.data
        // - タグ studentFilters.tagFilter.tags
        // - 権限の情報 operatorPrivileges.data
        // が必ず取得される
        this.loadMainResource();
      }
    }

    componentDidMount() {
      this.checkPrivileges();
    }

    componentDidUpdate(): void {
      if (this.props.student.data && this.props.params.studentId) {
        if (this.props.params.studentId !== this.props.student.data.id) {
          this.loadStudent(this.props.params.studentId);
        }
      }
    }

    render() {
      // 認証されていない状態
      if (!this.props.currentOperator) {
        return null;
      }

      return (
        <WrappedComponent
          {...this.props}
          onDateRangeFilterChange={this.handleDateRangeFilterChange}
          onDateFilterChange={this.handleDateFilterChange}
          onOrderChange={this.handleOrderChange}
          onSearchStudent={this.handleSearchStudent}
        />
      );
    }

    private handleDateRangeFilterChange = (
      dateRangeFilter: DateRangeFilterInterface,
    ) => {
      this.pushFilterHistory({ dateRangeFilter });
    };

    private handleDateFilterChange = (dateFilter: Date) => {
      this.pushFilterHistory({ dateFilter });
    };

    private handleOrderChange = (
      order: string,
      orderDir: OrderDirFilterType,
    ) => {
      this.pushFilterHistory({ orderDir, order });
    };

    private handleSearchStudent = (studentNameFilters: {
      firstName: string;
      lastName: string;
    }) => {
      const { firstName, lastName } = studentNameFilters;

      if (!this.sectionId) {
        return;
      }

      const query = queryString.stringify(
        {
          first_name: firstName,
          last_name: lastName,
          ...this.props.studentFilterContext.getStudentFilterParams(),
        },
        {
          arrayFormat: "bracket",
          skipEmptyString: true,
        },
      );

      this.props.navigate({
        pathname: `/sections/${this.sectionId}/settings/students`,
        search: query,
      });
    };

    private pushFilterHistory(obj: FiltersQueryInterface) {
      const filtersQuery: FiltersQueryInterface = { ...obj };

      const query = FiltersHelper.toQueryString(filtersQuery);

      this.props.navigate({ search: query });
    }

    // 校舎ページだったら校舎、生徒ページだったら生徒を読み込み
    private loadMainResource() {
      const { studentId, sectionId } = this.props.params;
      studentId && this.loadStudent(studentId);
      sectionId && this.loadSection(sectionId);
    }

    // 校舎読み込み
    private loadSection(sectionId: string) {
      const { props } = this;

      // currentSectionが存在しないとき or currentSectionのidとパスパラメータのsectionIdが異なるとき
      if (
        !props.currentSection.data ||
        sectionId !== props.currentSection.data.id
      ) {
        // redux storeを初期化
        props.resetApp();
        // 校舎の読込
        props.loadSection(sectionId);
        // 権限の読込
        props.loadOperatorPrivileges(sectionId, requiredPrivileges);
        // 生徒タグ読み込みは withStudentFilterContext 側のフックで実行
      } else if (!props.operatorPrivileges.data) {
        // アカウント切り替えでprivilegesがリセットされたときの権限チェック
        props.loadOperatorPrivileges(sectionId, requiredPrivileges);
      } else {
        // 通常時の権限チェック
        requiredPrivileges &&
          props.checkPrivileges(
            props.operatorPrivileges.data.privileges,
            requiredPrivileges,
          );
      }
    }

    // 生徒読み込み
    private loadStudent(studentId: string) {
      const { props } = this;

      // studentが存在しないとき or studentのidとパスパラメータのstudentIdがことなるとき
      if (!props.student.data || studentId !== props.student.data.id) {
        // redux storeを初期化
        props.resetApp();
        // 生徒，校舎，権限の読込
        props
          .loadStudent(studentId)
          .then((student: StudentInterface | void) => {
            if (student) {
              const sectionId = student.section.id;
              // 校舎の読込
              props.loadSection(sectionId);
              // 権限の読込
              props.loadOperatorPrivileges(sectionId);
            }
          });
      }
    }

    private checkPrivileges() {
      // 画面遷移のときの権限チェック
      if (this.props.operatorPrivileges.data && requiredPrivileges) {
        this.props.checkPrivileges(
          this.props.operatorPrivileges.data.privileges,
          requiredPrivileges,
        );
      }
    }

    private hasError(): boolean {
      return Object.keys(this.props.errors).some((key: string) => {
        return (this.props.errors as any)[key];
      });
    }
  }

  return withRouter<AuthenticatedPageProps>(
    connect(
      mapStateToProps,
      actions,
    )(withStudentFilterContext(AuthenticatedPage)),
  );
};

// 生徒フィルターのReact Contextを流すためのwrapper
// enhanceAuthenticatedPageでラップされるページがクラスコンポーネントの場合にuseStudentFilterContextを呼び出せないためpropsとして流し込む
const withStudentFilterContext = (
  Component: ComponentClass<AuthenticatedPageProps>,
) => {
  return (props: Omit<AuthenticatedPageProps, "studentFilterContext">) => {
    const studentFilterContext = useStudentFilterContext();
    const sectionIdFromParams = props?.params?.sectionId || null;
    const sectionIdFromStudent = props?.student?.data?.section?.id || null;
    const resolvedSectionId = sectionIdFromParams || sectionIdFromStudent;

    // NOTE: WEB-2245 対応。生徒画面遷移時に AuthenticatedPage コンストラクタが resetApp() を呼ぶと
    //       Redux 上で currentSection.data / student.data が瞬間的に null になり、
    //       resolvedSectionId も一時的に null にフリップする。これを filterKey に流すと
    //       useStudentFilterContext 側の useLayoutEffect [filterKey] が連鎖的に発火し、
    //       isTagLoaded / isInitialized が繰り返しリセットされ絞り込みが読み込み中で止まる原因になる。
    //       直近の非 null な sectionId を state で保持し、null になった瞬間はそれを維持することで
    //       中間状態の flicker を抑止する。
    //
    //       render 中の ref 書き込みは React 19 の Concurrent Rendering で discard された render の
    //       副作用が残留するリスクがあるため、useState + useLayoutEffect でコミット確定後に更新する。
    //       同一 レイアウト内 (StudentLayout / SectionLayout の中) での studentId 変化などのケースで
    //       効果がある。レイアウト跨ぎ (StudentLayout → SectionLayout) は component tree ごと
    //       リマウントされるため、この state も初期化される点は許容 (根本の resetApp() 副作用モデル
    //       改善は別 PR で検討)。
    const [lastResolvedSectionId, setLastResolvedSectionId] = useState<
      string | null
    >(null);
    useLayoutEffect(() => {
      if (resolvedSectionId) {
        setLastResolvedSectionId(resolvedSectionId);
      }
    }, [resolvedSectionId]);
    const stableSectionId = resolvedSectionId || lastResolvedSectionId;

    // 校舎IDが確定したタイミングでタグを初期化
    const { error } = useInitializeStudentTags({
      sectionId: stableSectionId,
      enabled: !!props?.currentOperator,
    });
    useQueryError(error);

    const newProps = {
      ...props,
      studentFilterContext,
    };
    return <Component {...newProps} />;
  };
};

export default enhanceAuthenticatedPage;
